import React from "react";
import {
  toWSDateString,
  useWSModalCustom,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSInputDate,
  WSList,
  WSModal,
  WSPill,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { queriesPayeeRows } from "@query";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { RequirementReviewStrategy } from "@wingspanhq/payments/dist/interfaces/requirement";
import { DataItem } from "../../../../shared/components/DataItem";
import {
  expirationExtensionOptions,
  extendExpirationDefaultValues,
  ExtendExpirationFormData,
  getExtendExpirationDate,
  getExtendExpirationValidationSchema
} from "../ExtendExpirationDateModal/utils";
import { useMutationUpdateRequirement } from "../../../../query/requirements/mutations/useMutationUpdateRequirement";
import { addDays, getStartOfDay } from "../../../../utils/dates";
import { useGetNow } from "../../../../shared/hooks/useGetNow";

export interface IRequirementWithEngagementIdsResponse extends IRequirementResponse {
  relatedEngagementRequirementIds: {
    engagementId: string;
    relatedRequirementId: string;
  }[];
}

export interface MarkRequirementAsCompleteModalProps {
  onClose: () => void;
  requirement: IRequirementResponse;
  context: ContextItem;
  additionalData?: RequirementAdditionalData;
  currentStatus: RequirementStatusViewProps;
  onSuccess: () => void;
}

export const MarkRequirementAsCompleteModal: React.FC<
  MarkRequirementAsCompleteModalProps
> = ({ onClose, requirement, context, onSuccess, currentStatus }) => {
  const now = useGetNow();

  const [changeRequirementStatus, changeRequirementStatusMeta] =
    useMutationUpdateRequirement();

  const [showExtendExpiration, setShowExtendExpiration] = React.useState(false);

  const xWingspanAccount = useXWingspanExpansion();
  const queryPayeeGeneralSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_requirementId: { eq: requirement.requirementId }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const contractorsLength = queryPayeeGeneralSummary.data || "-";

  const isApprovalFlow =
    requirement.reviewStrategy === RequirementReviewStrategy.AlwaysRequired ||
    requirement.reviewStrategy === RequirementReviewStrategy.OnFail;

  return (
    <WSForm<ExtendExpirationFormData>
      defaultValues={extendExpirationDefaultValues}
      validationSchema={getExtendExpirationValidationSchema(
        showExtendExpiration ? requirement.expirationDate : undefined
      )}
      onSubmit={formData => {
        const nextDate = getExtendExpirationDate(
          formData.extensionType,
          formData.customDate
        );

        changeRequirementStatus(
          {
            requirementId: requirement.requirementId,
            status: RequirementStatus.Complete,
            ...(showExtendExpiration
              ? {
                  expiresAt: nextDate
                }
              : {})
          },
          {
            onSuccess() {
              onSuccess();
              onClose();
            }
          }
        );
      }}
    >
      {({ watch }) => {
        const extensionType = watch("extensionType");
        const customDate = watch("customDate");

        const nextDate = getExtendExpirationDate(extensionType, customDate);

        const isNextDateEarlier =
          nextDate && requirement.expiresAt && nextDate < requirement.expiresAt;
        const isNextDate90DaysLate = nextDate && nextDate > addDays(now, 90);

        return (
          <WSModal
            title={
              isApprovalFlow
                ? "Approve requirement"
                : "Mark requirement as complete"
            }
            onClose={onClose}
            actions={{
              alignment: "fill",
              buttons: [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: onClose,
                  type: "button"
                },
                {
                  kind: "Primary",
                  label: isApprovalFlow ? "Yes, approve" : "Yes, mark complete",
                  type: "submit",
                  loading: changeRequirementStatusMeta.isLoading
                }
              ]
            }}
          >
            <WSList gap="XL">
              <WSFlexBox wrap="nowrap" gap="M">
                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Current status:
                  </WSText.ParagraphSm>
                  <WSPill {...currentStatus.pill}></WSPill>
                  <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                    {currentStatus.secondaryText}
                  </WSText.ParagraphXs>
                </WSElement>
                <WSIcon
                  style={{
                    marginTop: "auto",
                    marginBottom: "auto"
                  }}
                  name="arrow-right"
                />

                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Overwritten status preview:
                  </WSText.ParagraphSm>
                  <WSPill text="Complete" theme="success" icon></WSPill>
                  <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                    {isApprovalFlow
                      ? "Approved by you"
                      : "Marked complete by you"}
                  </WSText.ParagraphXs>
                </WSElement>
              </WSFlexBox>
              {requirement.expiresAt ? (
                <WSInfoBox py="L">
                  <WSList gap="M">
                    <WSFlexBox gap="M" wrap="nowrap" justify="space-between">
                      <WSElement>
                        <WSText.ParagraphXs color="gray500" mb="S">
                          Current expiration date
                        </WSText.ParagraphXs>
                        <WSText.ParagraphSm mt="XS" color="gray600">
                          {toWSDateString(requirement.expiresAt, "input")}
                        </WSText.ParagraphSm>
                      </WSElement>

                      <WSButton.Link
                        size="S"
                        onClick={() => setShowExtendExpiration(s => !s)}
                      >
                        {showExtendExpiration
                          ? "Cancel extension"
                          : "Extend expiration date"}
                      </WSButton.Link>
                    </WSFlexBox>

                    <WSText.ParagraphSm color="gray600">
                      This requirement has an expiration date extracted from the
                      document used to fulfill the requirement. Review and
                      confirm before marking the requirement complete or extend
                      the expiration date.
                    </WSText.ParagraphSm>
                    <WSElement hidden={!showExtendExpiration}>
                      <WSForm.Field
                        name="extensionType"
                        label="New expiration date"
                        component={WSSelect}
                        componentProps={{
                          options: expirationExtensionOptions
                        }}
                      />
                    </WSElement>
                    <WSInfoBox hidden={!showExtendExpiration}>
                      {extensionType === "custom" && (
                        <WSForm.Field
                          name="customDate"
                          label="Custom expiration date"
                          component={WSInputDate}
                          componentProps={{
                            minDate: getStartOfDay(1)
                          }}
                          mb="L"
                        />
                      )}

                      {nextDate && (
                        <DataItem
                          label="New expiration date"
                          value={toWSDateString(nextDate, "input")}
                        />
                      )}
                    </WSInfoBox>
                  </WSList>
                </WSInfoBox>
              ) : null}

              {isNextDateEarlier ? (
                <WSAlert
                  title="About moving the expiration earlier"
                  theme="warning"
                >
                  You are about to move the expiration to an earlier date than
                  than the original. Please ensure that the necessary
                  communication has been provided to the contractor to avoid
                  unexpected surprises and payment delays.
                </WSAlert>
              ) : isNextDate90DaysLate ? (
                <WSAlert
                  title="New expiration date is over 90 days out"
                  theme="warning"
                >
                  You are about to move the expiration to a future date over 90
                  days out. Please double check the new expiration date.
                  Incomplete requirements may result in payment delays.
                </WSAlert>
              ) : null}

              <WSInfoBox py="L">
                <WSText.ParagraphSm color="gray600">
                  By proceeding, the requirement status will be marked complete
                  by you.
                  <ul>
                    <li>The status will be updated to “Complete”</li>
                  </ul>
                </WSText.ParagraphSm>

                <WSText.ParagraphSm color="gray600">
                  This requirement is assigned to {contractorsLength} of the
                  contractor’s engagements and may affect their eligibility to
                  receive payments from you. Would you like to proceed?
                </WSText.ParagraphSm>
              </WSInfoBox>
              <WSErrorMessage
                mt="XL"
                contextKey="Other"
                error={changeRequirementStatusMeta.isError}
              />
            </WSList>
          </WSModal>
        );
      }}
    </WSForm>
  );
};

export const useMarkRequirementAsCompleteModal = () =>
  useWSModalCustom(MarkRequirementAsCompleteModal);
